import api from '@pray/shared/utils/api';

export type SfCheckStatus = 'green' | 'yellow' | 'red' | 'info';

export type SfCheck = {
  id: string;
  name: string;
  value: string;
  status: SfCheckStatus;
  detail: string;
  // Lookback sparkline series (monthly, chronological) + change vs prior month, and which
  // direction counts as "good" (drives delta-chip color). Absent for non-numeric checks.
  trend?: number[];
  delta?: number | null;
  higher_is_better?: boolean;
  unit?: string;
};

export type SfMonthRow = {
  month: string;
  n_subs: number;
  n_ever_paid: number;
  ever_pay_pct: number;
  n_trial_started: number;
  trial_start_pct: number;
  gross_cents: number;
  net_cents: number;
  maturity: 'mature' | 'maturing' | 'immature';
};

export type SfDeclineRow = {
  segment: string;
  current_total: number;
  current_failed: number;
  current_pct: number;
  baseline_total: number;
  baseline_failed: number;
  baseline_pct: number | null;
};

export type SfCampaignRankRow = {
  campaign_id: number;
  leader: string | null;
  base_pct: number;
  base_n: number;
  current_pct: number;
  current_n: number;
  drop_pp: number;
};

export type SfCampaignRef = { campaign_id: number; leader: string | null };

export type SfDistRow = { segment: string; count: number };
export type SfDistribution = { funding: SfDistRow[]; source: SfDistRow[] };

export type SfSubscriptionDashboard = {
  snapshot_date: string | null;
  scope: 'fleet' | 'campaign';
  campaign_id: number;
  current_month: string | null;
  baseline_month: string | null;
  checks: SfCheck[];
  campaigns: SfCampaignRef[];
  distribution: SfDistribution;
  cohort_monthly: SfMonthRow[];
  per_campaign_rank: SfCampaignRankRow[];
  decline_funding: SfDeclineRow[];
  decline_source: SfDeclineRow[];
  decline_brand: SfDeclineRow[];
  reason_fleet: Array<{ reason: string; subs: number }>;
  comparator_nonsf: SfMonthRow[];
};

export type GetSfSubscriptionsParams = {
  scope?: 'fleet' | 'campaign';
  campaignId?: number | null;
  // Report signup window, 'YYYY-MM-DD'. cohortTo is the report "as of" date.
  cohortFrom?: string | null;
  cohortTo?: string | null;
};

export async function getSfSubscriptions({
  scope = 'fleet',
  campaignId = null,
  cohortFrom = null,
  cohortTo = null,
}: GetSfSubscriptionsParams = {}): Promise<SfSubscriptionDashboard> {
  const params: Record<string, string | number | boolean> = { scope };

  if (scope === 'campaign' && campaignId != null) params.campaign_id = campaignId;
  if (cohortFrom) params.cohort_from = cohortFrom;
  if (cohortTo) params.cohort_to = cohortTo;

  const url = '/web/studio/system-check/superfunnel/subscriptions';
  const response = await api.executeGetRequest<{ data: SfSubscriptionDashboard }>(url, params);

  return response.data.data;
}
